import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type PageTopBarPartial = {
  breadcrumbs?: React.ReactNode | null;
  /** The record the page shows, for the browser tab's title (PageHeader's `recordTitle`). */
  recordTitle?: string | null;
  /** SF-T-266: on a phone the page's own header takes the top bar's place (a chat), so the bar is hidden. */
  hiddenOnPhone?: boolean;
};

interface PageTopBarContextValue {
  breadcrumbs: React.ReactNode | null;
  recordTitle: string | null;
  hiddenOnPhone: boolean;
  setPageTopBar: (partial: PageTopBarPartial) => void;
}

const PageTopBarContext = createContext<PageTopBarContextValue | null>(null);

/**
 * The top bar's breadcrumbs and record title, set by the page's PageHeader. `embedded`: a page shown
 * in the link dialog (SF-T-160), which shows them in its own header, with no sticky top bar above.
 */
export function PageTopBarProvider({ children, embedded = false }: { children: React.ReactNode; embedded?: boolean }) {
  const [breadcrumbs, setBreadcrumbs] = useState<React.ReactNode | null>(null);
  const [recordTitle, setRecordTitle] = useState<string | null>(null);
  const [hiddenOnPhone, setHiddenOnPhone] = useState(false);

  const setPageTopBar = useCallback(
    (partial: PageTopBarPartial) => {
      if ('breadcrumbs' in partial) {
        setBreadcrumbs((prev) =>
          partial.breadcrumbs === prev ? prev : (partial.breadcrumbs ?? null)
        );
      }
      if ('recordTitle' in partial) setRecordTitle(partial.recordTitle ?? null);
      if ('hiddenOnPhone' in partial) setHiddenOnPhone(partial.hiddenOnPhone ?? false);
    },
    []
  );

  const value = useMemo(
    () => ({
      breadcrumbs,
      recordTitle,
      hiddenOnPhone,
      setPageTopBar,
    }),
    [breadcrumbs, recordTitle, hiddenOnPhone, setPageTopBar]
  );

  return (
    <PageTopBarContext.Provider value={value}>
      <div
        data-slot="app-shell"
        className={
          embedded
            ? 'flex min-h-0 w-full flex-1 flex-col md:h-full [--app-topbar-height:0px]'
            : 'flex min-h-0 w-full flex-1 flex-col [--app-topbar-height:3rem]'
        }
      >
        {children}
      </div>
    </PageTopBarContext.Provider>
  );
}

export function usePageTopBarOptional() {
  return useContext(PageTopBarContext);
}
